import "react-toastify/dist/ReactToastify.css";
import Button from "../shared/components/buttons";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Input } from "../shared/components/input";
import { z, ZodError } from "zod";
import DateDropdowns, {
  DateError,
} from "../components/child-details/date-dropdowns";
import { PageProps } from "shared/defines";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import {
  childDobState,
  childFirstNameState,
  childLastNameState,
} from "../state/child-details";
import { TextDecoder } from "../shared/text-effects/TextDecoder";
import dayjs from "dayjs";
import useCreateDisplayName from "../shared/hooks/useCreateDisplayName";
import { DebouncedName } from "../components/child-details/DebouncedName";

const nameSchema = z.object({
  firstName: z
    .string()
    .min(1, { message: "First name is required" })
    .max(50, { message: "First name must be less than 50 characters" }),
  lastName: z
    .string()
    .min(1, { message: "Last name is required" })
    .max(50, { message: "Last name must be less than 50 characters" }),
});

const AGE_VALIDATION_MESSAGE = `Please check the date of birth. You only need to complete the online waiver for players under 18 years of age (also check you have entered the child's name in the name fields above).`;

const ChildDetailsPage: FC<PageProps> = ({ title }) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastNameError, setLastNameError] = useState<string | null>(null);

  const [firstName, setFirstName] = useRecoilState(childFirstNameState);
  const [lastName, setLastName] = useRecoilState(childLastNameState);
  const displayName = useCreateDisplayName();

  const [dateError, setDateError] = useState<DateError>({
    month: false,
    day: false,
    year: false,
  });

  const [age18Error, setAge18Error] = useState<string | null>(null);

  const [birthDate, setBirthDate] = useRecoilState(childDobState); // iso format YYYY-MM-DD

  const handleEnter = useCallback((event: KeyboardEvent) => {
    if (event.key === "Enter") {
      btnRef.current?.click();
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleEnter);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleEnter);
    };
  }, [handleEnter]);

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    setFirstNameError(null);
  }, [firstName]);

  useEffect(() => {
    setLastNameError(null);
  }, [lastName]);

  const resetError = () => {
    setFirstNameError(null);
    setLastNameError(null);
    setAge18Error(null);
  };

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    resetError();

    const data = { firstName, lastName };

    try {
      nameSchema.parse(data);

      const dateValues = birthDate.split("-").map((v) => Number(v));
      if (dateValues.length !== 3 || dateValues.some((v) => v === 0)) {
        setDateError({
          month: !dateValues[1],
          day: !dateValues[2],
          year: !dateValues[0],
        });
        return;
      }

      // Check if the child is 18 or older
      const now = dayjs(); // current date
      const age = now.diff(dayjs(birthDate), "year");

      if (age >= 18) {
        setAge18Error(AGE_VALIDATION_MESSAGE);
        return;
      }

      navigate("/waiver-signature");
    } catch (e) {
      const dateValues = birthDate.split("-").map((v) => Number(v));
      if (dateValues.length !== 3 || dateValues.some((v) => v === 0)) {
        setDateError({
          month: !dateValues[1],
          day: !dateValues[2],
          year: !dateValues[0],
        });
      }

      const issues = (e as ZodError).issues;
      issues.forEach((issue) => {
        if (issue.path[0] === "firstName") {
          setFirstNameError(issue.message);
        }
        if (issue.path[0] === "lastName") {
          setLastNameError(issue.message);
        }
      });
    }
  };

  const isDisabledContinue =
    !!age18Error ||
    !!firstNameError ||
    !!lastNameError ||
    dateError.month ||
    dateError.day ||
    dateError.year;

  return (
    <form
      noValidate
      onSubmit={handleFormSubmit}
      className="w-full flex-1 justify-start sm:justify-center flex flex-col gap-10"
    >
      <div className="whitespace-pre flex sm:h-14 h-8.5">
        <TextDecoder
          className="font-aeonik font-medium text-[2.125rem]/[2.125rem] sm:text-[3.1rem]/[3.4rem] gap-5"
          delay={0}
          duration={500}
          animationType={"in"}
          text="Child details"
        />
      </div>
      <div className="flex flex-col w-full">
        <Input
          className="flex-1"
          size="sm"
          name="firstName"
          label="Child first name"
          error={firstNameError}
          onChange={setFirstName}
          value={firstName}
        />
        <Input
          className="flex-1 mt-3"
          size="sm"
          name="lastName"
          label="Child last name"
          error={lastNameError}
          onChange={setLastName}
          value={lastName}
        />
        {displayName && (
          <DebouncedName
            text={`Your child's leaderboard display name will be: ${displayName}`}
          />
        )}

        <div className="w-full mt-3">
          <span className="text-[1.25rem] font-medium">
            Child date of birth
          </span>

          <div className="w-full gap-2.5 flex pt-1">
            <DateDropdowns
              value={birthDate}
              onChange={(day: string, month: string, year: string) => {
                const dayNum = Number(day);
                const monthNum = Number(month);
                const yearNum = Number(year);

                const newBirthDate = `${yearNum || ""}-${
                  monthNum ? month : ""
                }-${dayNum ? day : ""}`;

                setBirthDate(newBirthDate);

                const now = dayjs();
                const age = now.diff(dayjs(newBirthDate), "year");

                if (age >= 18) {
                  setAge18Error(AGE_VALIDATION_MESSAGE);
                } else {
                  setAge18Error(null);
                }

                if (dateError.day || dateError.month || dateError.year) {
                  setDateError({
                    month: !monthNum,
                    day: !dayNum,
                    year: !yearNum,
                  });
                }
              }}
              error={dateError}
            />
          </div>
        </div>
        {age18Error && (
          <div className="text-red-error text-[1.2rem] mt-4">{age18Error}</div>
        )}
        <Button
          ref={btnRef}
          disabled={isDisabledContinue}
          variant="filled"
          size="sm"
          type="submit"
          className="text-[1.2rem] w-full sm:w-57.5 mt-10"
        >
          Continue
        </Button>
      </div>
    </form>
  );
};

export default ChildDetailsPage;
